// FILE: src/components/modals/MyOrdersModal.jsx

import React from 'react';
import { X, Package, Clock, CheckCircle, Truck, MapPin } from 'lucide-react';
import { getOrderItemImage } from '../../utils/imageHelpers';
import orderHelpers from '../../utils/orderHelpers';

const MyOrdersModal = ({ isOpen, onClose, orders, addToCart, showToast, loadCart }) => {
  if (!isOpen) return null;

  // Debug logging to check order data structure
  console.log('📦 MyOrdersModal - Orders data:', orders);
  if (orders && orders.length > 0) {
    console.log('📦 First order address fields:', {
      deliveryAddress: orders[0].deliveryAddress,
      delivery_address: orders[0].delivery_address,
      address: orders[0].address
    });
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'In Transit':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Processing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleTrackOrder = async (order) => {
    try {
      console.log('🚚 Tracking order:', order);
      const orderIdentifier = order.order_number || order.id;
      console.log('🔑 Using order identifier:', orderIdentifier);
      
      const result = await orderHelpers.trackOrder(orderIdentifier);
      console.log('📦 Track result:', result);
      
      if (result.success && result.tracking) {
        const tracking = result.tracking;
        // Format tracking info for display
        const status = tracking.status || order.status || 'Unknown';
        const location = tracking.current_location || tracking.location || 'In transit';
        const estimatedDelivery = tracking.estimated_delivery || tracking.delivery_date || 'Not available';
        
        const trackingMessage = `Status: ${status}\nLocation: ${location}\nEstimated Delivery: ${estimatedDelivery}`;
        showToast && showToast(trackingMessage, 'success');
      } else {
        showToast && showToast(result.message || 'Failed to track order', 'error');
      }
    } catch (error) {
      console.error('❌ Track order error:', error);
      showToast && showToast('Failed to track order: ' + error.message, 'error');
    }
  };

  const handleReorder = async (order) => {
    try {
      const result = await orderHelpers.reorder(order.id);
      if (result.success) {
        // Reload cart from backend to reflect the new items
        if (loadCart) {
          await loadCart();
        }
        showToast && showToast('Items added to cart!', 'success');
        // Close modal and switch to cart
        onClose();
      } else {
        showToast && showToast(result.message || 'Failed to reorder', 'error');
      }
    } catch (error) {
      showToast && showToast('Failed to reorder', 'error');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle size={16} />;
      case 'In Transit':
        return <Truck size={16} />;
      case 'Processing':
        return <Clock size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  // Helper function to format address (handles both string and object)
  const formatAddress = (order) => {
    // Check if deliveryAddress is a string
    if (typeof order.deliveryAddress === 'string' && order.deliveryAddress) {
      return order.deliveryAddress;
    }
    
    // Check if delivery_address is a string
    if (typeof order.delivery_address === 'string' && order.delivery_address) {
      return order.delivery_address;
    }
    
    // Handle address object
    const addressObj = order.deliveryAddress || order.delivery_address || order.address;
    
    if (addressObj && typeof addressObj === 'object') {
      // If there's a full_address field, use it
      if (addressObj.full_address) {
        return addressObj.full_address;
      }
      
      // Otherwise, construct address from parts
      const parts = [
        addressObj.address,
        addressObj.landmark,
        addressObj.city,
        addressObj.state,
        addressObj.pincode
      ].filter(Boolean);
      
      return parts.length > 0 ? parts.join(', ') : 'Address not available';
    }
    
    return 'Address not available';
  };

  return (
    <div className="fixed inset-0 z-[300]" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6"
        style={{ maxWidth: 448, margin: '0 auto', maxHeight: '85vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: 301 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">My Orders</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-all"
          >
            <X size={24} />
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No orders yet</p>
            <p className="text-gray-400 text-sm mt-2">Start shopping to see your orders here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div 
                key={order.id} 
                className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-all"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm text-gray-500">Order #{order.id}</p>
                    <p className="text-xs text-gray-400">{order.date}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    {order.status}
                  </span>
                </div>

                <div className="space-y-3 mb-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 justify-between text-sm">
                      <div className="flex items-center gap-2 flex-1">
                        {getOrderItemImage(item, 'small')}
                        <div className="flex-1">
                          <span className="text-gray-700 font-medium block">
                            {item.name}({item.price ? `₹${item.price}` : 'Price not available'})  
                          </span>
                          <span className="text-xs text-gray-500">
                            Qty: {item.quantity}
                          </span>
                        </div>
                      </div>
                      <span className="text-gray-900 font-medium">
                        ₹{item.subtotal || (item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Delivery Details */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin size={14} className="text-gray-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Delivery Address</p>
                      <p className="text-sm text-gray-800 font-medium">
                        {formatAddress(order)}
                      </p>
                    </div>
                  </div>
                  {order.deliverySlot && (
                    <div className="text-xs text-gray-600 pl-6">
                      Time Slot: {order.deliverySlot}
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">Total</span>
                  <span className="text-lg font-bold text-blue-600">₹{order.total}</span>
                </div>

                <div className="flex gap-2 mt-3">
                  <button 
                    onClick={() => handleTrackOrder(order)}
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-all"
                  >
                    Track Order
                  </button>
                  <button 
                    onClick={() => handleReorder(order)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                  >
                    Reorder
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersModal;